import { useState } from "react";
import { Loader2, ShieldCheck, ShieldAlert, CheckCircle2, XCircle, X, FileCheck2, Gavel, Sparkles } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { renderMarkdown } from "@/lib/md";

const Mono = ({ children, className = "" }) => (
  <span className={`font-mono uppercase tracking-[0.2em] ${className}`}>{children}</span>
);

const EVIDENCE = [
  ["form1_complete", "Form 1 — Preliminary Damage Assessment complete"],
  ["engineer_verified", "Damage verified by engineer / quantity surveyor"],
  ["prepost_evidence", "Pre- & post-disaster condition evidence (photos)"],
  ["competitive_procurement", "Competitive procurement (no cost-plus-%)"],
  ["invoices_attached", "Itemised contractor invoices attached"],
  ["within_deadline", "Within 12-month lodgement deadline"],
  ["threshold_met", "Financial threshold met"],
  ["betterment_form", "Form 3 — Betterment submitted"],
  ["acquittal_ready", "Acquittal evidence ready (proof of funds & outcomes)"],
];

const EMPTY = {
  agrn: "", council: "", claim_type: "EPAR", asset_name: "", essential_asset: true,
  damage_description: "", estimated_cost: 0, betterment: false, submission_text: "",
  form1_complete: false, engineer_verified: false, prepost_evidence: false,
  competitive_procurement: false, invoices_attached: false, within_deadline: false,
  threshold_met: false, betterment_form: false, acquittal_ready: false,
};

const SEV = { blocker: "text-red-400", major: "text-gold-400", minor: "text-white/50" };

export default function RAAgent() {
  const [mode, setMode] = useState("assemble"); // assemble | evaluate
  const [form, setForm] = useState(EMPTY);
  const [qa, setQa] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const payload = () => ({ ...form, estimated_cost: Number(form.estimated_cost) || 0 });

  const runQA = async () => {
    setError(""); setBusy("qa");
    try { setQa((await api.post("/raagent/qa", payload())).data); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(""); }
  };
  const run = async (endpoint, key) => {
    setError(""); setBusy(key);
    try {
      const res = await api.post(`/raagent/${endpoint}`, payload());
      setQa(res.data.qa); setResult(res.data.document);
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(""); }
  };

  const scoreColor = qa ? (qa.blockers ? "text-red-400" : qa.majors ? "text-gold-400" : "text-emerald-400") : "text-white";

  return (
    <div className="space-y-8 text-white" data-testid="raagent-page">
      <div>
        <Mono className="text-gold-400 text-[0.65rem]">Sheet 03 — RAAgent · NSWRA DRFA / SmartyGrants</Mono>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3">
          <h1 className="font-heading font-light text-4xl tracking-tight">Recovery Assistance Agent</h1>
          <a
            href="https://raagent.cbkm.au"
            target="_blank"
            rel="noreferrer"
            data-testid="raagent-standalone-link"
            className="inline-flex items-center gap-1.5 font-mono uppercase tracking-[0.15em] text-[0.55rem] text-gold-400 border border-gold-500/30 hover:border-gold-500/60 hover:text-gold-300 px-2.5 py-1 rounded-sm transition-colors"
          >
            Open standalone · raagent.cbkm.au <Sparkles className="h-3 w-3" />
          </a>
        </div>
        <p className="text-white/55 mt-3 max-w-2xl font-light">
          Assemble Category B (EPAR / EW / IRW) claims that pass every DRFA test, or evaluate a submission the way
          NSWRA would — with QA checks for everything so it can't be denied.
        </p>
      </div>

      {/* mode tabs */}
      <div className="inline-flex border border-gold-500/25 bg-navy-900/40 p-1 rounded-sm">
        {[["assemble", "Build & QA", FileCheck2], ["evaluate", "Evaluate submission", Gavel]].map(([k, label, Icon]) => (
          <button
            key={k}
            onClick={() => setMode(k)}
            data-testid={`mode-${k}`}
            className={`flex items-center gap-2 px-5 py-2.5 text-sm rounded-sm transition-colors ${
              mode === k ? "bg-gold-500 text-navy-950 font-semibold" : "text-white/60 hover:text-white"
            }`}
          >
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-300 text-sm px-4 py-3">{error}</div>}

      <div className="grid lg:grid-cols-12 gap-6">
        {/* submission form */}
        <div className="lg:col-span-7 border border-gold-500/25 bg-navy-900/40 p-6 space-y-5">
          <Mono className="text-gold-400 text-[0.6rem] block">Claim details</Mono>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="AGRN / event ref" v={form.agrn} on={(v) => set("agrn", v)} tid="ra-agrn" ph="e.g. AGRN1198" />
            <Field label="Council / entity" v={form.council} on={(v) => set("council", v)} tid="ra-council" />
            <div>
              <Mono className="text-white/60 text-[0.55rem] block mb-2">Claim type</Mono>
              <select value={form.claim_type} onChange={(e) => set("claim_type", e.target.value)} data-testid="ra-type" className="w-full bg-navy-950 border border-gold-500/20 px-3 py-3 text-white focus:outline-none focus:border-gold-500">
                {["EPAR", "EW", "IRW", "CDO"].map((c) => <option key={c} className="bg-navy-900">{c}</option>)}
              </select>
            </div>
            <Field label="Estimated cost ($)" type="number" v={form.estimated_cost} on={(v) => set("estimated_cost", v)} tid="ra-cost" />
            <Field label="Asset name" v={form.asset_name} on={(v) => set("asset_name", v)} tid="ra-asset" />
            <div className="flex items-end gap-4 pb-1">
              <Toggle label="Essential public asset" on={form.essential_asset} set={(v) => set("essential_asset", v)} tid="ra-essential" />
              <Toggle label="Betterment" on={form.betterment} set={(v) => set("betterment", v)} tid="ra-betterment" />
            </div>
          </div>
          <div>
            <Mono className="text-white/60 text-[0.55rem] block mb-2">Damage description</Mono>
            <textarea rows={3} value={form.damage_description} onChange={(e) => set("damage_description", e.target.value)} data-testid="ra-damage" className="w-full bg-navy-950 border border-gold-500/20 px-4 py-3 text-white focus:outline-none focus:border-gold-500 resize-none" />
          </div>

          <Mono className="text-gold-400 text-[0.6rem] block pt-1">Evidence checklist</Mono>
          <div className="grid sm:grid-cols-2 gap-y-2 gap-x-4">
            {EVIDENCE.map(([k, label]) => (
              <label key={k} data-testid={`ev-${k}`} className="flex items-start gap-2.5 cursor-pointer group">
                <input type="checkbox" checked={form[k]} onChange={(e) => set(k, e.target.checked)} className="mt-0.5 accent-[#D4AF37] h-4 w-4" />
                <span className="text-sm text-white/70 group-hover:text-white">{label}</span>
              </label>
            ))}
          </div>

          {mode === "evaluate" && (
            <div>
              <Mono className="text-white/60 text-[0.55rem] block mb-2">Existing submission text (optional — paste to assess)</Mono>
              <textarea rows={4} value={form.submission_text} onChange={(e) => set("submission_text", e.target.value)} data-testid="ra-submission-text" className="w-full bg-navy-950 border border-gold-500/20 px-4 py-3 text-white focus:outline-none focus:border-gold-500 resize-none" />
            </div>
          )}

          <div className="flex flex-wrap gap-3 pt-2">
            <button onClick={runQA} disabled={busy} data-testid="run-qa-btn" className="border border-gold-500/40 text-gold-300 font-semibold px-5 py-3 rounded-sm hover:bg-gold-500/10 transition-colors flex items-center gap-2 disabled:opacity-60">
              {busy === "qa" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Run QA checks
            </button>
            {mode === "assemble" ? (
              <button onClick={() => run("assemble", "assemble")} disabled={busy} data-testid="assemble-btn" className="bg-gold-500 text-navy-950 font-semibold px-5 py-3 rounded-sm hover:bg-gold-400 transition-colors flex items-center gap-2 disabled:opacity-60">
                {busy === "assemble" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Assemble denial-proof submission
              </button>
            ) : (
              <button onClick={() => run("evaluate", "evaluate")} disabled={busy} data-testid="evaluate-btn" className="bg-gold-500 text-navy-950 font-semibold px-5 py-3 rounded-sm hover:bg-gold-400 transition-colors flex items-center gap-2 disabled:opacity-60">
                {busy === "evaluate" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gavel className="h-4 w-4" />} Evaluate as assessor
              </button>
            )}
          </div>
        </div>

        {/* QA panel */}
        <div className="lg:col-span-5 border border-gold-500/25 bg-navy-900/40 p-6">
          <Mono className="text-gold-400 text-[0.6rem] block mb-4">Submission readiness</Mono>
          {!qa ? (
            <div className="text-white/45 text-sm font-light py-10 text-center">
              <ShieldAlert className="h-9 w-9 text-gold-500/50 mx-auto mb-3" />
              Run QA to score this claim against every DRFA test.
            </div>
          ) : (
            <>
              <div className="flex items-end justify-between">
                <div>
                  <div className={`font-mono text-5xl ${scoreColor}`} data-testid="qa-score">{qa.score}%</div>
                  <div className={`text-sm mt-1 font-medium ${scoreColor}`} data-testid="qa-verdict">{qa.verdict}</div>
                </div>
                <div className="text-right">
                  <div className="text-red-400 font-mono text-sm">{qa.blockers} blockers</div>
                  <div className="text-gold-400 font-mono text-sm">{qa.majors} majors</div>
                </div>
              </div>
              <div className="h-2 bg-navy-950 border border-gold-500/20 mt-4 overflow-hidden">
                <div className={`h-full ${qa.blockers ? "bg-red-500" : qa.majors ? "bg-gold-500" : "bg-emerald-400"}`} style={{ width: `${qa.score}%` }} />
              </div>
              <div className="mt-5 space-y-2.5 max-h-[360px] overflow-y-auto pr-1">
                {qa.checks.map((c) => (
                  <div key={c.id} className="flex items-start gap-2.5" data-testid={`qa-check-${c.id}`}>
                    {c.passed ? <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" /> : <XCircle className={`h-4 w-4 mt-0.5 shrink-0 ${SEV[c.severity]}`} />}
                    <div>
                      <div className={`text-sm ${c.passed ? "text-white/75" : "text-white"}`}>{c.label}</div>
                      {!c.passed && <div className="text-xs text-white/45 mt-0.5">{c.fix}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* result modal */}
      {result && (
        <div className="fixed inset-0 bg-navy-950/80 z-50 flex items-center justify-center p-4" onClick={() => setResult(null)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-navy-900 border border-gold-500/30 w-full max-w-3xl max-h-[85vh] flex flex-col" data-testid="ra-result-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gold-500/20">
              <h2 className="font-heading text-xl text-white">{result.title}</h2>
              <button onClick={() => setResult(null)}><X className="h-5 w-5 text-white/60" /></button>
            </div>
            <div className="overflow-y-auto p-2">
              <div className="bg-white p-8 md-body" dangerouslySetInnerHTML={{ __html: renderMarkdown(result.content) }} />
            </div>
            <div className="px-6 py-3 border-t border-gold-500/20">
              <Mono className="text-white/40 text-[0.55rem]">Saved to your documents · print / export from your browser</Mono>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, v, on, tid, type = "text", ph }) {
  return (
    <div>
      <Mono className="text-white/60 text-[0.55rem] block mb-2">{label}</Mono>
      <input type={type} value={v} placeholder={ph} onChange={(e) => on(e.target.value)} data-testid={tid} className="w-full bg-navy-950 border border-gold-500/20 px-3 py-3 text-white focus:outline-none focus:border-gold-500 placeholder:text-white/30" />
    </div>
  );
}
function Toggle({ label, on, set, tid }) {
  return (
    <button type="button" onClick={() => set(!on)} data-testid={tid} className="flex items-center gap-2 text-sm">
      <span className={`h-5 w-9 rounded-full transition-colors relative ${on ? "bg-gold-500" : "bg-white/20"}`}>
        <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-navy-950 transition-all ${on ? "left-[1.15rem]" : "left-0.5"}`} />
      </span>
      <span className="text-white/70">{label}</span>
    </button>
  );
}
